import { useMemo, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import "@fontsource/rubik";
import CustomAlert from '../../events/components/CustomAlert'
import CustomButton from '../../../components/CustomButton'
import { updateReviewState } from '../../../redux/reviewSlice'
import { revisionHistoryErrorMessage, sortVersionsForDisplay } from '../../../utils/revisionHistory'
import useRevisionHistory from '../hooks/useRevisionHistory'
import RevisionHistoryVersionCard from './RevisionHistoryVersionCard'
import RevisionHistoryVersionDetail from './RevisionHistoryVersionDetail'

/**
 * Read-only Revision History for the current project.
 *
 * Mounting the popup triggers the fetch, and closing it unmounts the state, so
 * the presigned `video_url` / `download_url` links are never reused across
 * opens - they expire after 5 days without a CDN, and notes edited on the live
 * revision panel would otherwise show stale.
 */
const RevisionHistoryPopup = () => {
  const dispatch = useDispatch()
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const eventId = commonReducer?.production_event_id;

  const { versions, notesVisible, isLoading, error, fetchHistory } = useRevisionHistory(eventId, true);
  const [selectedVideoId, setSelectedVideoId] = useState(null);

  // Newest version on top, without mutating the ascending array the API sent.
  const orderedVersions = useMemo(() => sortVersionsForDisplay(versions), [versions]);

  // Derived, not stored: the newest version is shown until one is picked, and a
  // refetch that drops the open version (a new delivery, say) falls back to the
  // newest instead of leaving an empty panel.
  const selectedVersion =
    orderedVersions.find((version) => version?.final_video_id === selectedVideoId) || orderedVersions[0] || null;

  const handleClose = () => {
    dispatch(updateReviewState({ revision_history_popup: false }))
  }

  return (
    <CustomAlert
      open={true}
      handleClose={handleClose}
      maxWidth='lg'
      width={{ xs: '600px', sm: '680px', md: '760px', lg: '900px', xl: '1300px' }}
      height={{ xs: '460px', sm: '500px', md: '560px', lg: '620px', xl: '860px' }}
    >
      <Box data-testid='revision-history-popup' sx={{ width: '100%', padding: { xs: '8px', lg: '12px', xl: '20px' } }}>
        <Typography
          sx={{ fontFamily: 'Rubik', fontWeight: 600, color: 'black', textAlign: 'left', fontSize: { xs: '14px', md: '16px', lg: '18px', xl: '24px' }, mb: 2 }}
        >
          Revision History
        </Typography>

        {isLoading ? (
          <Box data-testid='revision-history-loading' className='flex j-center a-center' sx={{ height: { xs: '200px', xl: '320px' } }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box data-testid='revision-history-error' className='flex col j-center a-center' gap={2} sx={{ height: { xs: '200px', xl: '320px' } }}>
            <Typography sx={{ fontFamily: 'Rubik', color: '#08231B', fontSize: { xs: '10px', md: '12px', lg: '13px', xl: '18px' } }}>
              {revisionHistoryErrorMessage(error?.http_code, error?.message)}
            </Typography>
            {/* 403 and 404 are terminal for this project - only retry what a retry can fix. */}
            {error?.http_code !== 403 && error?.http_code !== 404 && (
              <CustomButton
                btnText='Retry'
                data-testid='revision-history-retry'
                onClick={() => fetchHistory()}
                component='button'
                type='button'
                color='secondary'
                variant='contained'
                style={{ borderRadius: '5px', border: 'none', textTransform: 'none' }}
                sx={{ fontFamily: 'Rubik', fontWeight: 600, width: { xs: '90px', xl: '140px' }, fontSize: { xs: '9px', lg: '11px', xl: '16px' } }}
              />
            )}
          </Box>
        ) : orderedVersions.length === 0 ? (
          <Box data-testid='revision-history-empty' className='flex j-center a-center' sx={{ height: { xs: '200px', xl: '320px' } }}>
            <Typography sx={{ fontFamily: 'Rubik', color: '#08231B', fontSize: { xs: '10px', md: '12px', lg: '13px', xl: '18px' } }}>
              No versions have been delivered yet
            </Typography>
          </Box>
        ) : (
          <Box className='flex' gap={2} sx={{ width: '100%', alignItems: 'flex-start' }}>
            <Box
              className='flex col'
              sx={{ width: '30%', maxHeight: { xs: '360px', xl: '560px' }, overflowY: 'auto', paddingRight: '4px' }}
            >
              {orderedVersions.map((version) => (
                <RevisionHistoryVersionCard
                  key={version?.final_video_id}
                  version={version}
                  selected={version?.final_video_id === selectedVersion?.final_video_id}
                  onClick={() => setSelectedVideoId(version?.final_video_id)}
                />
              ))}
            </Box>

            <Box sx={{ width: '70%', maxHeight: { xs: '360px', xl: '560px' }, overflowY: 'auto', paddingRight: '4px' }}>
              <RevisionHistoryVersionDetail version={selectedVersion} notesVisible={notesVisible} />
            </Box>
          </Box>
        )}
      </Box>
    </CustomAlert>
  )
}

export default RevisionHistoryPopup
